/**
 * Turn-by-turn navigation on top of the hazard map. Owns the route, the
 * destination, the instruction card and the route-aware "ahead" test.
 *
 * What it is not: live traffic, ETA from crowd speeds, rerouting around
 * congestion. Those need GPS density Albania does not supply; the ETA here is
 * the engine's static estimate and is labelled as such nowhere — it is simply
 * shown as minutes, which is how every driver reads it anyway.
 */
import type maplibregl from "maplibre-gl";
import { navClipUrl, type Alert, type Fix } from "../../shared/alerts.js";
import {
  distanceText, durationText, indexRoute, instructionText, nextManeuver, projectOnRoute, routeDistanceTo,
  OFF_ROUTE_M, type Maneuver, type Route, type RouteIndex,
} from "../../shared/route.js";
import type { Session } from "./session.js";
import { loadSettings } from "./settings.js";
import { LocalRouter } from "./localrouter.js";

export type Destination = { name: string; lat: number; lon: number };

export type NavDeps = {
  session: Session;
  /** Live delay (seconds) on the route ahead, from the traffic feed. */
  delayAhead: (idx: RouteIndex, alongM: number) => number;
  getMap: () => maplibregl.Map | null;
  getGl: () => typeof maplibregl | null;
  /** Called when the route changes, so the map and simulator can react. */
  onRoute: (idx: RouteIndex | null) => void;
  onArrive: () => void;
};

const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (el === null) throw new Error(`missing element #${id}`);
  return el as T;
};

/** Which recorded fragment says this manoeuvre (see scripts/voice-lines.json). */
export function navFragment(m: Maneuver): string | null {
  switch (m.kind) {
    case "depart": return null;
    case "arrive": return "arrite";
    case "uturn": return "kthehu-mbrapa";
    case "merge": return "bashkohu";
    case "roundabout": return m.exit !== null && m.exit >= 1 && m.exit <= 5 ? `rrethrrotullim-${m.exit}` : "rrethrrotullim";
    case "exit_roundabout": return null;
    case "straight": return "vazhdo-drejt";
    case "keep": case "fork": return m.modifier === null || m.modifier === "straight" ? "vazhdo-drejt" : `mbaje-${m.modifier.includes("left") ? "majtas" : "djathtas"}`;
    case "turn": {
      switch (m.modifier) {
        case "left": return "kthehu-majtas";
        case "right": return "kthehu-djathtas";
        case "slight_left": return "kthehu-lehte-majtas";
        case "slight_right": return "kthehu-lehte-djathtas";
        case "sharp_left": return "kthehu-fort-majtas";
        case "sharp_right": return "kthehu-fort-djathtas";
        default: return "vazhdo-drejt";
      }
    }
  }
}

/** The nearest recorded distance line. */
export function distanceBucket(m: number): string {
  if (m < 150) return "pas-100m";
  if (m < 250) return "pas-200m";
  if (m < 400) return "pas-300m";
  if (m < 750) return "pas-500m";
  if (m < 1500) return "pas-1km";
  return "pas-2km";
}

/** All fragment clips, for preloading at drive start. */
export const NAV_FRAGMENTS = [
  "pas-100m", "pas-200m", "pas-300m", "pas-500m", "pas-1km", "pas-2km", "tani",
  "kthehu-majtas", "kthehu-djathtas", "kthehu-lehte-majtas", "kthehu-lehte-djathtas", "kthehu-fort-majtas", "kthehu-fort-djathtas",
  "mbaje-majtas", "mbaje-djathtas", "vazhdo-drejt", "rrethrrotullim", "rrethrrotullim-1", "rrethrrotullim-2", "rrethrrotullim-3",
  "rrethrrotullim-4", "rrethrrotullim-5", "kthehu-mbrapa", "bashkohu", "arrite", "jashte-rruges", "kufiri",
];

/** One arrow, rotated per manoeuvre. Roundabouts, U-turns and arrival get their own. */
const ARROW = `<svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"><path d="M24 42V10M12 22l12-12 12 12"/></svg>`;
const ROUNDABOUT = `<svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"><circle cx="24" cy="26" r="11"/><path d="M24 44v-7M24 15V4M16 10l8-6 8 6"/></svg>`;
const UTURN = `<svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"><path d="M32 42V16a8 8 0 0 0-16 0v10M9 20l7 7 7-7"/></svg>`;
const ARRIVE = `<svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"><circle cx="24" cy="20" r="12"/><circle cx="24" cy="20" r="3" fill="currentColor"/><path d="M24 32v12"/></svg>`;

function glyphFor(m: Maneuver): string {
  if (m.kind === "roundabout" || m.kind === "exit_roundabout") return ROUNDABOUT;
  if (m.kind === "uturn") return UTURN;
  if (m.kind === "arrive") return ARRIVE;
  const rot: Record<string, number> = {
    left: -90, right: 90, slight_left: -40, slight_right: 40, sharp_left: -135, sharp_right: 135, straight: 0,
  };
  const deg = m.modifier === null ? 0 : rot[m.modifier] ?? 0;
  return `<span style="display:block;transform:rotate(${deg}deg)">${ARROW}</span>`;
}

export class RouteGuide {
  private idx: RouteIndex | null = null;
  private dest: Destination | null = null;
  private destMarker: maplibregl.Marker | null = null;
  private offRouteFixes = 0;
  private lastRerouteAt = 0;
  private rerouting = false;
  private arrivedAt: number | null = null;
  private lastCardKey = "";
  private choices: Route[] = [];
  /** Which manoeuvre the earcons were last played for, so each rings once. */
  private chimed: { index: number; far: boolean; near: boolean } = { index: -1, far: false, near: false };
  private chime: (kind: "far" | "near") => void = () => undefined;
  /** Speaks a fragment sequence; returns false when the clips are not there. */
  private say: (urls: string[]) => boolean = () => false;
  /** Our own router, on the phone. The server route is only the fallback. */
  readonly local = new LocalRouter();

  constructor(private readonly deps: NavDeps) {
    $("navEnd").addEventListener("click", () => this.stop());
    $("routeChoices").addEventListener("click", (ev) => {
      const b = (ev.target as HTMLElement).closest<HTMLButtonElement>("button[data-route]");
      if (b === null) return;
      this.choose(Number(b.dataset["route"]));
    });
  }

  /** Earcons and speech come from the voice module, which owns the AudioContext. */
  setVoice(chime: (kind: "far" | "near") => void, say: (urls: string[]) => boolean): void {
    this.chime = chime;
    this.say = say;
  }

  /** "Pas treqind metrash, kthehu majtas." from static fragments. Street names
   *  are on the card, never spoken: no Albanian TTS exists and a recorded
   *  fragment set cannot say every street in the country. */
  private announce(step: Maneuver, distanceM: number | null): void {
    const frag = navFragment(step);
    if (frag === null) return;
    const urls: string[] = [];
    if (distanceM !== null) urls.push(navClipUrl(distanceBucket(distanceM)));
    else if (step.kind !== "arrive") urls.push(navClipUrl("tani"));
    urls.push(navClipUrl(frag));
    if (!this.say(urls)) this.chime(distanceM !== null ? "far" : "near");
  }

  get active(): boolean {
    return this.idx !== null;
  }

  get route(): RouteIndex | null {
    return this.idx;
  }

  get destination(): Destination | null {
    return this.dest;
  }

  /** Routes currently offered in the chooser (0 when none is open). */
  get offered(): number {
    return this.choices.length;
  }

  /** Ask the Worker for a route and start guiding. Resolves to an Albanian error
   *  line when it cannot, so the caller can show it verbatim. */
  async start(from: Fix, dest: Destination): Promise<string | null> {
    this.dest = dest;
    this.placeDestination(dest);
    const err = await this.fetchRoute(from, dest);
    if (err !== null) {
      // No route, but the destination still stands: a straight-line beam is
      // better than nothing when routing is offline.
      this.clearRoute();
      return err;
    }
    return null;
  }

  stop(): void {
    this.dest = null;
    this.destMarker?.remove();
    this.destMarker = null;
    this.clearRoute();
    this.hideChoices();
    $("navCard").hidden = true;
    this.arrivedAt = null;
  }

  private clearRoute(): void {
    this.idx = null;
    this.offRouteFixes = 0;
    this.lastCardKey = "";
    this.drawRoute(null);
    this.deps.onRoute(null);
  }

  private async fetchRoute(from: Fix, dest: Destination, alternatives = true): Promise<string | null> {
    const settings = loadSettings();
    const opts = { alternatives, avoidTolls: settings.avoidTolls };
    let routes: Route[];
    const local = await this.local.route(from, dest, opts);
    if (local.kind === "ok") {
      routes = local.routes;
    } else if (local.kind === "no_route") {
      return "Nuk u gjet rrugë deri atje.";
    } else if (local.kind === "off_network") {
      return "Pika është larg çdo rruge të njohur.";
    } else {
      // The graph could not be loaded (first run offline, or a bad download):
      // ask the server, which routes through the same graph's cousin.
      const res = await this.deps.session.signed("/v1/route", { from: { lat: from.lat, lon: from.lon }, to: { lat: dest.lat, lon: dest.lon }, ...opts });
      if (res === null) return "Nuk ka lidhje. Provo përsëri kur të kesh internet.";
      if (res.status === 503) return "Harta e rrugëve nuk u shkarkua ende. Provo përsëri me internet.";
      if (res.status === 404) return "Nuk u gjet rrugë deri atje.";
      if (res.status !== 200) return "Serveri i rrugëve nuk u përgjigj. Provo përsëri.";
      routes = (res.json as { routes: Route[] }).routes;
    }
    if (routes.length === 0) return "Nuk u gjet rrugë deri atje.";
    if (routes.length > 1 && alternatives) {
      this.showChoices(routes);
      return null;
    }
    this.useRoute(routes[0] as Route);
    return null;
  }

  private useRoute(route: Route): void {
    this.idx = indexRoute(route);
    this.offRouteFixes = 0;
    this.lastCardKey = "";
    this.chimed = { index: -1, far: false, near: false };
    this.drawRoute(route);
    this.drawAlternative(null);
    this.deps.onRoute(this.idx);
    $("navCard").hidden = false;
  }

  /** Two routes on the map, two rows to tap. The first is the engine's best. */
  private showChoices(routes: Route[]): void {
    this.choices = routes;
    const first = routes[0] as Route;
    const second = routes[1] as Route;
    this.drawRoute(first);
    this.drawAlternative(second);
    const label = (r: Route, i: number): string => {
      const tag = i === 0
        ? (r.durationS <= second.durationS ? "më e shpejta" : "më e shkurtra")
        : (r.distanceM < first.distanceM ? "më e shkurtra" : r.durationS < first.durationS ? "më e shpejta" : "tjetër");
      return `<b>${durationText(r.durationS)}</b> · ${distanceText(r.distanceM)} <small>${tag}</small>`;
    };
    $("routeChoices").innerHTML = routes.map((r, i) => `<button type="button" data-route="${i}">${label(r, i)}</button>`).join("");
    $("routeChoice").hidden = false;
  }

  private hideChoices(): void {
    this.choices = [];
    $("routeChoice").hidden = true;
    this.drawAlternative(null);
  }

  private choose(i: number): void {
    const r = this.choices[i];
    if (r === undefined) return;
    this.hideChoices();
    this.useRoute(r);
  }

  /** Per GPS fix: progress, the card, off-route detection, arrival. Returns the
   *  route-distance test the alert engine should use, or null off-route. */
  tick(fix: Fix): ((alert: Alert) => number | null) | null {
    if (this.idx === null || this.dest === null) return null;
    const idx = this.idx;
    const p = projectOnRoute(idx, fix);
    const total = idx.cum[idx.cum.length - 1] as number;

    // Arrival: within 40 m of the end, or past it.
    if (total - p.alongM < 40 && p.offM < 80) {
      if (this.arrivedAt === null) {
        this.arrivedAt = fix.at;
        this.renderCard(ARRIVE, "", "Arrite në destinacion", this.dest.name, "");
      } else if (fix.at - this.arrivedAt > 6000) {
        this.stop();
        this.deps.onArrive();
      }
      return null;
    }

    // Not yet on the route: the router snaps the start to the nearest road, which
    // can be a few hundred metres from where the phone actually is (a car park,
    // a square, GPS drift). That is not "off route" — it is "walk/drive to it".
    if (p.offM > OFF_ROUTE_M && p.alongM < 40 && p.offM < 600) {
      const first = nextManeuver(idx, 0);
      const toRoute = distanceText(p.offM);
      const etaAll = new Date(fix.at + idx.route.durationS * 1000).toLocaleTimeString("sq-AL", { hour: "2-digit", minute: "2-digit", hour12: false });
      const metaAll = `${durationText(idx.route.durationS)} · ${distanceText(idx.route.distanceM)} · ${etaAll}`;
      if (first !== null) this.renderCard(glyphFor(first.step), `Rruga fillon pas ${toRoute}`, instructionText(first.step), first.step.name, metaAll);
      else this.renderCard(ARROW, `Rruga fillon pas ${toRoute}`, "Nisu drejt rrugës", "", metaAll);
      return null;
    }

    // Off route: not one noisy fix, five in a row.
    if (p.offM > OFF_ROUTE_M) {
      this.offRouteFixes++;
      if (this.offRouteFixes >= 5) void this.reroute(fix);
      return null;
    }
    this.offRouteFixes = 0;

    const next = nextManeuver(idx, p.alongM);
    const remainingM = total - p.alongM;
    const delayS = this.deps.delayAhead(idx, p.alongM);
    const remainingS = idx.route.durationS * (remainingM / Math.max(1, total)) + delayS;
    const eta = new Date(fix.at + remainingS * 1000).toLocaleTimeString("sq-AL", { hour: "2-digit", minute: "2-digit", hour12: false });
    const jam = delayS >= 60 ? ` · +${durationText(delayS)} trafik` : "";
    const meta = `${durationText(remainingS)} · ${distanceText(remainingM)} · ${eta}${jam}`;
    if (next !== null) {
      // One tone at ~300 m, two at ~60 m, each once per manoeuvre.
      if (this.chimed.index !== next.index) this.chimed = { index: next.index, far: false, near: false };
      // Far announcement scales with speed (about 12 s out, 150–1000 m), the near
      // one is fixed at 60 m. Each once per manoeuvre.
      const farAt = Math.max(150, Math.min(1000, fix.speedMps * 12));
      if (!this.chimed.far && next.distanceM <= farAt && next.step.kind !== "arrive") { this.chimed.far = true; this.announce(next.step, next.distanceM); }
      if (!this.chimed.near && next.distanceM <= 60) { this.chimed.near = true; this.announce(next.step, null); }
      const text = instructionText(next.step);
      // The road line only adds something when the sentence does not already name it.
      this.renderCard(glyphFor(next.step), `Pas ${distanceText(next.distanceM)}`, text, text.includes(next.step.name) ? "" : next.step.name, meta);
    }
    return (alert: Alert) => routeDistanceTo(idx, p.alongM, alert);
  }

  private async reroute(fix: Fix): Promise<void> {
    if (this.rerouting || this.dest === null) return;
    if (fix.at - this.lastRerouteAt < 20_000) return;
    this.rerouting = true;
    this.lastRerouteAt = fix.at;
    try {
      this.renderCard(ARROW, "", "Po llogarit rrugën përsëri…", "", "");
      if (!this.say([navClipUrl("jashte-rruges")])) this.chime("near");
      // A reroute takes the best route silently: the driver is moving.
      const err = await this.fetchRoute(fix, this.dest, false);
      if (err !== null) this.renderCard(ARROW, "", "Jashtë rrugës", err, "");
    } finally {
      this.rerouting = false;
      this.offRouteFixes = 0;
    }
  }

  private renderCard(glyph: string, distance: string, text: string, road: string, meta: string): void {
    const key = `${glyph.length}|${distance}|${text}|${road}|${meta}`;
    if (key === this.lastCardKey) return;
    this.lastCardKey = key;
    $("navGlyph").innerHTML = glyph;
    $("navDistance").textContent = distance;
    $("navText").textContent = text;
    const roadEl = $("navRoad");
    roadEl.textContent = road;
    roadEl.hidden = road === "";
    $("navMeta").textContent = meta;
    $("navCard").hidden = false;
  }

  private drawRoute(route: Route | null): void {
    const map = this.deps.getMap();
    if (map === null) return;
    const src = map.getSource("route") as maplibregl.GeoJSONSource | undefined;
    if (src === undefined) return;
    src.setData(route === null
      ? { type: "FeatureCollection", features: [] }
      : { type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: route.geometry.map(([lat, lon]) => [lon, lat]) } });
  }

  private drawAlternative(route: Route | null): void {
    const map = this.deps.getMap();
    if (map === null) return;
    const src = map.getSource("route-alt") as maplibregl.GeoJSONSource | undefined;
    if (src === undefined) return;
    src.setData(route === null
      ? { type: "FeatureCollection", features: [] }
      : { type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: route.geometry.map(([lat, lon]) => [lon, lat]) } });
  }

  private placeDestination(dest: Destination): void {
    const map = this.deps.getMap();
    const gl = this.deps.getGl();
    if (map === null || gl === null) return;
    if (this.destMarker === null) {
      const el = document.createElement("div");
      el.className = "dest";
      this.destMarker = new gl.Marker({ element: el, anchor: "center" });
    }
    this.destMarker.setLngLat([dest.lon, dest.lat]).addTo(map);
  }
}

/* ---------------- search ---------------- */

export type SearchDeps = {
  session: Session;
  getFix: () => Fix | null;
  onPick: (dest: Destination) => void;
};

type Hit = { name: string; detail: string; lat: number; lon: number; kind: string };

/* ---------------- saved places ---------------- */

type Places = { home: Destination | null; work: Destination | null; recent: Destination[] };
const PLACES_KEY = "fanar.places";

export function loadPlaces(): Places {
  try {
    const raw = localStorage.getItem(PLACES_KEY);
    if (raw === null) return { home: null, work: null, recent: [] };
    const p = JSON.parse(raw) as Partial<Places>;
    return { home: p.home ?? null, work: p.work ?? null, recent: Array.isArray(p.recent) ? p.recent.slice(0, 6) : [] };
  } catch { return { home: null, work: null, recent: [] }; }
}
export function savePlaces(p: Places): void {
  try { localStorage.setItem(PLACES_KEY, JSON.stringify(p)); } catch { /* storage blocked */ }
}
export function rememberDestination(d: Destination): void {
  const p = loadPlaces();
  p.recent = [d, ...p.recent.filter((r) => r.name !== d.name || Math.abs(r.lat - d.lat) > 1e-4)].slice(0, 6);
  savePlaces(p);
}

/** "Ku shkon?" — one input, results as you type, one tap to go. Saved places
 *  (Shtëpia, Puna) and the last destinations sit above the results. */
const esc = (t: string): string => t.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string);

export function wireSearch(deps: SearchDeps): { open: () => void; close: () => void } {
  const sheet = $("search");
  const input = $<HTMLInputElement>("searchInput");
  const list = $("searchList");
  const state = $("searchState");
  const shortcuts = $("searchShortcuts");
  let seq = 0;
  let timer: number | null = null;
  /** When set, the next pick is stored under this slot instead of navigated to. */
  let saving: "home" | "work" | null = null;

  const close = (): void => { sheet.hidden = true; input.blur(); saving = null; };
  const renderShortcuts = (): void => {
    const p = loadPlaces();
    const slot = (key: "home" | "work", label: string): string => {
      const d = p[key];
      return `<button type="button" class="search__slot" data-slot="${key}"><b>${label}</b><span>${d === null ? "Cakto" : esc(d.name)}</span></button>`;
    };
    shortcuts.innerHTML = slot("home", "Shtëpia") + slot("work", "Puna") +
      p.recent.map((r, i) => `<button type="button" class="search__recent" data-recent="${i}">${esc(r.name)}</button>`).join("");
  };
  const open = (): void => {
    sheet.hidden = false;
    input.value = "";
    list.innerHTML = "";
    saving = null;
    renderShortcuts();
    state.textContent = "Shkruaj një qytet, fshat ose rrugë.";
    window.setTimeout(() => input.focus(), 50);
  };
  const pick = (d: Destination): void => {
    if (saving !== null) {
      const p = loadPlaces(); p[saving] = d; savePlaces(p); saving = null;
      renderShortcuts(); input.value = ""; list.innerHTML = "";
      state.textContent = "U ruajt. Ku shkon?";
      return;
    }
    rememberDestination(d);
    close();
    deps.onPick(d);
  };
  shortcuts.addEventListener("click", (ev) => {
    const b = (ev.target as HTMLElement).closest<HTMLButtonElement>("button");
    if (b === null) return;
    const p = loadPlaces();
    if (b.dataset["slot"] !== undefined) {
      const key = b.dataset["slot"] as "home" | "work";
      const d = p[key];
      if (d === null) { saving = key; state.textContent = key === "home" ? "Kërko dhe zgjidh shtëpinë." : "Kërko dhe zgjidh punën."; input.focus(); return; }
      pick(d);
    } else if (b.dataset["recent"] !== undefined) {
      const d = p.recent[Number(b.dataset["recent"])];
      if (d !== undefined) pick(d);
    }
  });
  shortcuts.addEventListener("contextmenu", (ev) => {
    // Long-press / right-click a slot: forget it, so it can be set again.
    const b = (ev.target as HTMLElement).closest<HTMLButtonElement>("button[data-slot]");
    if (b === null) return;
    ev.preventDefault();
    const p = loadPlaces(); p[b.dataset["slot"] as "home" | "work"] = null; savePlaces(p); renderShortcuts();
  });

  const render = (hits: Hit[]): void => {
    list.innerHTML = "";
    for (const h of hits) {
      const li = document.createElement("li");
      const b = document.createElement("button");
      b.type = "button";
      b.className = "search__hit";
      b.innerHTML = `<span class="search__name"></span><span class="search__detail"></span>`;
      (b.firstElementChild as HTMLElement).textContent = h.name;
      (b.lastElementChild as HTMLElement).textContent = h.detail;
      b.addEventListener("click", () => pick({ name: h.name, lat: h.lat, lon: h.lon }));
      li.appendChild(b);
      list.appendChild(li);
    }
    state.textContent = hits.length === 0 ? "Asnjë rezultat." : "";
  };

  const query = async (q: string): Promise<void> => {
    const mine = ++seq;
    state.textContent = "Duke kërkuar…";
    const f = deps.getFix();
    const res = await deps.session.signed("/v1/geocode", { q, lat: f?.lat, lon: f?.lon });
    if (mine !== seq) return;
    if (res === null || res.status !== 200) { state.textContent = "Kërkimi nuk punon pa internet."; return; }
    render((res.json as { hits: Hit[] }).hits);
  };

  input.addEventListener("input", () => {
    const q = input.value.trim();
    if (timer !== null) window.clearTimeout(timer);
    if (q.length < 2) { list.innerHTML = ""; state.textContent = "Shkruaj një qytet, fshat ose rrugë."; return; }
    timer = window.setTimeout(() => void query(q), 350);
  });
  input.addEventListener("keydown", (ev) => { if (ev.key === "Enter") { const first = list.querySelector<HTMLButtonElement>("button"); first?.click(); } });
  $("searchCancel").addEventListener("click", close);
  $("searchOpen").addEventListener("click", open);
  return { open, close };
}
